import type { EditResidentHouseholdFormValues, PetFormValues } from './types';
import { isEmpty, isNil, prop, equals, not, find } from 'ramda';
import {
  validatePhoneDuplicates,
  validateEmailDuplicates,
} from '../../../utils/duplicateValidationUtils';

const validateRequired = (property: string, obj: Object) =>
  not(isEmpty(prop(property, obj))) &&
  not(isNil(prop(property, obj))) &&
  !equals('default', prop(property, obj));

const validate = (
  values: EditResidentHouseholdFormValues,
  petTypeList: Array<any>,
  applicantTypesOptions: Array<Object>,
  intl?: Object,
) => {
  return {
    adults: values.adults.map((adult, adultIndex) => {
      const errors = {};

      if (adult.id === values.primaryResidentId) {
        const type: any = applicantTypesOptions.find(
          (applicantType) => applicantType.value === adult.type,
        );
        if (!type.financiallyResponsible) {
          errors.type = 'Primary/HOH must be a financial responsible type';
        }
      }

      // Check for duplicates within contactInformation arrays (new format)
      if (adult.contactInformation) {
        // Validate phone duplicates within this adult
        if (
          adult.contactInformation.phones &&
          Array.isArray(adult.contactInformation.phones)
        ) {
          const phonesErrors = validatePhoneDuplicates(
            adult.contactInformation.phones,
            intl,
          );

          if (
            phonesErrors.some(
              (phoneError) => Object.keys(phoneError).length > 0,
            )
          ) {
            if (!errors.contactInformation) errors.contactInformation = {};
            errors.contactInformation.phones = phonesErrors;
          }
        }

        // Validate email duplicates within this adult
        if (
          adult.contactInformation.emails &&
          Array.isArray(adult.contactInformation.emails)
        ) {
          const emailsErrors = validateEmailDuplicates(
            adult.contactInformation.emails,
            intl,
          );

          if (
            emailsErrors.some(
              (emailError) => Object.keys(emailError).length > 0,
            )
          ) {
            if (!errors.contactInformation) errors.contactInformation = {};
            errors.contactInformation.emails = emailsErrors;
          }
        }
      }

      return errors;
    }),
    pets: values.pets.map((pet: PetFormValues) => {
      const errors = {};

      ['petTypeId', 'petBreedId'].forEach((fieldName) => {
        if (!validateRequired(fieldName, pet))
          errors[fieldName] = 'This field is required';
      });

      const selectedPetType =
        find((type) => type.value === pet.petTypeId, petTypeList) || {};

      if (
        !errors.petTypeId &&
        selectedPetType.requiresWeight &&
        !validateRequired('weight', pet)
      ) {
        errors.weight = 'This field is required';
      }

      if (!errors.weight && !isNil(pet.weight) && pet.weight <= 0) {
        errors.weight = 'Invalid value.';
      }

      return errors;
    }),
    pendingPets: values.pendingPets
      ? values.pendingPets.map((pet: PetFormValues) => {
          const errors = {};

          ['petTypeId', 'petBreedId'].forEach((fieldName) => {
            if (!validateRequired(fieldName, pet))
              errors[fieldName] = 'This field is required';
          });
          const selectedPetType =
            find((type) => type.value === pet.petTypeId, petTypeList) || {};

          if (
            !errors.petTypeId &&
            selectedPetType.requiresWeight &&
            !validateRequired('weight', pet)
          ) {
            errors.weight = 'This field is required';
          }

          if (!errors.weight && !isNil(pet.weight) && pet.weight <= 0) {
            errors.weight = 'Invalid value.';
          }

          return errors;
        })
      : [],
  };
};

export default validate;
